import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { usePagination } from '../../hooks/usePagination';
import TablePagination from '../../components/ui/table-pagination';

const AdminPagesList = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    type: 'standard',
    category_id: '',
    content: '',
    component: '',
    meta_title: '',
    meta_description: '',
    is_published: true,
    sort_order: 0,
  });

  const availableComponents = [
    { value: 'none', label: 'None (Static content only)' },
    { value: 'ContactForm', label: 'Contact Form' },
    { value: 'Newsletter', label: 'Newsletter Signup' },
  ];

  useEffect(() => {
    fetchPages();
    fetchCategories();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const data = await adminApi.pages.getAll(token);
      setPages(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load pages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const data = await adminApi.pageCategories.getAll(token);
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const handleOpenDialog = (page = null) => {
    if (page) {
      setEditingPage(page);
      // Extract from first version (similar to AdminProductsList pattern)
      const firstVersion = page.versions?.[0] || {};
      setFormData({
        title: firstVersion.title || '',
        slug: page.slug,
        type: page.type || 'standard',
        category_id: page.categoryId || '',
        content: firstVersion.content || '',
        component: page.component || '',
        meta_title: firstVersion.metaTitle || '',
        meta_description: firstVersion.metaDescription || '',
        is_published: page.isPublished !== undefined ? page.isPublished : true,
        sort_order: page.sortOrder || 0,
      });
    } else {
      setEditingPage(null);
      setFormData({
        title: '',
        slug: '',
        type: 'standard',
        category_id: '',
        content: '',
        component: '',
        meta_title: '',
        meta_description: '',
        is_published: true,
        sort_order: 0,
      });
    }
    setIsDialogOpen(true);
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (editingPage) {
        await adminApi.pages.update(editingPage.slug, formData, token);
        toast({
          title: 'Success',
          description: 'Page updated successfully',
        });
      } else {
        await adminApi.pages.create(formData, token);
        toast({
          title: 'Success',
          description: 'Page created successfully',
        });
      }
      setIsDialogOpen(false);
      fetchPages();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save page',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (pageId, pageTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${pageTitle}"?`)) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      await adminApi.pages.delete(pageId, token);
      toast({
        title: 'Success',
        description: 'Page deleted successfully',
      });
      fetchPages();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete page',
        variant: 'destructive',
      });
    }
  };

  // Pagination
  const {
    currentPage,
    totalPages,
    currentItems: paginatedPages,
    goToPage,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(pages, 20);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-serif text-[#2C2420]">Pages</h1>
          <p className="text-gray-600 mt-1">{totalItems} pages</p>
        </div>
        <Button onClick={() => navigate('/admin/pages/new')} className="bg-[#be1f1a] hover:bg-[#a71b17]">
          <Plus className="h-4 w-4 mr-2" />
          Add Page
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {paginatedPages.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-gray-500">No pages found</td>
              </tr>
            ) : (
              paginatedPages.map((page) => {
                // Extract title from first version
                const displayTitle = page.versions?.[0]?.title || 'Untitled';
                return (
                  <tr key={page.slug}>
                    <td className="px-6 py-4 font-medium">{displayTitle}</td>
                    <td className="px-6 py-4">{page.slug}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${page.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {page.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/${page.slug}`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/pages/edit/${page._id || page.id}`)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(page.id || page._id, displayTitle)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        
        {/* Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={goToPage}
        />
      </div>

      {/* Add/Edit Page Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPage ? 'Edit Page' : 'Add Page'}</DialogTitle>
            <DialogDescription>
              {editingPage ? 'Update page details' : 'Create a new page'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => {
                    const newTitle = e.target.value;
                    setFormData({
                      ...formData,
                      title: newTitle,
                      slug: !editingPage ? generateSlug(newTitle) : formData.slug,
                    });
                  }}
                  required
                />
                {formData.type === 'question' && (
                  <p className="text-xs text-gray-500 mt-1">This will be the FAQ question</p>
                )}
              </div>
              <div>
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Page Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Page</SelectItem>
                    <SelectItem value="question">FAQ Question</SelectItem>
                  </SelectContent>
                </Select>
                {formData.type === 'question' && (
                  <p className="text-xs text-green-600 mt-1">✓ Will appear in FAQ page</p>
                )}
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map((cat) => {
                      // Extract name from versions array
                      const displayName = cat.versions?.[0]?.name || cat.name || cat.slug;
                      return (
                        <SelectItem key={cat.id || cat._id} value={cat.id || cat._id}>
                          {displayName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.type === 'standard' && (
              <div>
                <Label htmlFor="component">Component</Label>
                <Select
                  value={formData.component || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, component: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select component" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableComponents.map((comp) => (
                      <SelectItem key={comp.value} value={comp.value}>
                        {comp.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="content">
                {formData.type === 'question' ? 'Answer *' : 'Content'}
              </Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={formData.type === 'question' ? 4 : 6}
                placeholder={formData.type === 'question' ? 'Enter the answer to this FAQ question...' : 'Page content (supports HTML)'}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="meta_title">Meta Title (SEO)</Label>
                <Input
                  id="meta_title"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="meta_description">Meta Description (SEO)</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label htmlFor="is_published" className="font-normal cursor-pointer">
                Published
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {editingPage ? 'Update' : 'Create'} Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPagesList;
